import Axios, { AxiosResponse } from "axios"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import {
  encryptionController,
  EncryptionKeys,
  setTabCurrentConversationEncryptionKey,
} from "controllers/EncryptionController"
import { activityLogHelper, ActivityLogType } from "helpers/activity_log"
import {
  getPrivateKeyByUserId,
  replaceImagesWithText,
  prepareEditorContentForEmail,
  getFileNameWithTimestamp,
} from "helpers/utils"
import { DECRYPTION_BLOCKED_CONTENT } from "helpers/decryptionBlocked"
import {
  captureSentryEncryptionEvent,
  generateTRCCode,
  addBreadcrumb,
  getSentryUserContext,
} from "helpers/sentryMonitoring"
import { STATUS_RESPONSE } from "types"
import {
  BuildConversationDetail,
  BuildInviteesConversationDetail,
  CONVERSATION_ROLE,
  ConversationDetail,
  ConversationMessagesDetail,
  CustomStatus,
  InviteeAddColumnDetail,
  Note,
  ParamsBuildConversationProps,
  ConversationShareKeys,
  ConversationKeyResponse,
  emptyConversationMessagesDetail,
} from "./conversations.type"
import { ComponentType } from "pages/project-component-detail/types"
import { ACTION_WORKER } from "workers/type"
import { SendComponentInviteeRequest } from "pages/project-build/project-build.type"
import { isUndefined } from "lodash"
import juice from "juice"
import { messageConversationCss } from "constants/message"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getAccessTokenInLocalStorage } from "helpers/access_token"
import {
  ApprovalStatus,
  MODEL_ACTIVITY_LOG,
} from "components/activity-log/activity-log.type"

export const getListConversationByBuildMiddleware = async (
  urlTab: string,
  params?: ParamsBuildConversationProps
) => {
  const response: AxiosResponse<{
    data: BuildConversationDetail[]
  }> = await Axios.get(`/api/conversation/${urlTab}`, { params })
  return response.data.data
}
export const getListConversationMiddleware = async (
  params?: ParamsBuildConversationProps
) => {
  const response: AxiosResponse<{
    data: ConversationDetail[]
    encryptedKeys?: {
      conversation_id: string
      encrypted_key: string
      additional_id?: string
    }[]
    projectKeys?: {
      encryptedKey: string
      id: string
    }[]
  }> = await Axios.get(`/api/conversation/all`, {
    params,
  })
  let projectEncryptionKeys = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  if (response.data.projectKeys && response.data.projectKeys.length) {
    const userVaultKey = localStorage.getItem(
      EncryptionKeys.userVaultKey
    ) as string
    const privateKey: any = await getPrivateKeyByUserId()
    for (const el of response.data.projectKeys) {
      let decryptedKey = await encryptionGCMHelper.decryptContext(
        userVaultKey,
        el.encryptedKey
      )
      if (decryptedKey === el.encryptedKey) {
        decryptedKey = privateKey.decrypt(el.encryptedKey, "RSA-OAEP")
      }
      projectEncryptionKeys[el.id] = decryptedKey
    }
    localStorage.setItem(
      EncryptionKeys.projectEncryptionKeys,
      JSON.stringify({
        ...projectEncryptionKeys,
      })
    )
  }

  if (response.data.encryptedKeys && response.data.encryptedKeys.length) {
    const privateKey: any = await getPrivateKeyByUserId()
    let conversationKeyLocal = JSON.parse(
      localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
    )
    for (const conversationKey of response.data.encryptedKeys) {
      if (conversationKey.additional_id) {
        const projectKey = projectEncryptionKeys[conversationKey.additional_id]
        if (projectKey) {
          const newDecryptedKey = await encryptionGCMHelper.decryptContext(
            projectKey,
            conversationKey.encrypted_key
          )
          if (newDecryptedKey !== conversationKey.encrypted_key) {
            conversationKeyLocal[conversationKey.conversation_id] =
              newDecryptedKey as string
          } else {
            try {
              const newDecryptedKeyPrivate = privateKey.decrypt(
                conversationKey.encrypted_key,
                "RSA-OAEP"
              )
              if (newDecryptedKeyPrivate !== conversationKey.encrypted_key) {
                conversationKeyLocal[conversationKey.conversation_id] =
                  newDecryptedKeyPrivate
              }
            } catch (error) {}
          }
        }
      } else {
        try {
          const decryptedKey = privateKey.decrypt(
            conversationKey.encrypted_key,
            "RSA-OAEP"
          )
          conversationKeyLocal[conversationKey.conversation_id] = decryptedKey
        } catch (error) {
          conversationKeyLocal[conversationKey.conversation_id] = ""
        }
      }
    }

    localStorage.setItem(
      EncryptionKeys.conversationEncryptionKeys,
      JSON.stringify(conversationKeyLocal)
    )

    addBreadcrumb("decrypt", "Decrypting conversation list", "info", {
      conversation_count: response.data.data.length,
    })
    return await Promise.all(
      response.data.data.map(async (item) => {
        const decryptedKey = conversationKeyLocal[item.id]
        const newMessage = { ...item.message, content: "" }
        const messageDecryptKey =
          item.message.replyKeyConversation || decryptedKey
        if (messageDecryptKey && item.message.content) {
          const result = (await encryptionGCMHelper.decryptContext(
            messageDecryptKey,
            item.message.content
          )) as string
          if (result === item.message.content) {
            const trcCode = generateTRCCode("DEC")
            addBreadcrumb("decrypt", "Conversation item decrypt failed", "warning")
            captureSentryEncryptionEvent(
              "Content decryption failed - WRONG_VALUE",
              {
                error_category: "DEC",
                error_type: "WRONG_VALUE",
                ...getSentryUserContext(),
                error_code: trcCode,
                step_trail: "conversations.api → getListConversationMiddleware",
              }
            )
            newMessage.content = DECRYPTION_BLOCKED_CONTENT
          } else {
            newMessage.content = result
          }
        }

        return {
          ...item,
          message: newMessage,
        }
      })
    )
  }
  localStorage.setItem(
    EncryptionKeys.conversationEncryptionKeys,
    JSON.stringify({})
  )

  return response.data.data
}
export const getConversationByBuildMiddleware = async (
  projectBuildId: string,
  urlConversationByBuild: string
) => {
  const response: AxiosResponse<{
    data: BuildInviteesConversationDetail[]
  }> = await Axios.get(
    `/api/conversation/${urlConversationByBuild}/${projectBuildId}`
  )
  return response.data.data
}
export const getConversationMessagesMiddleware = async (
  conversationId: string,
  dbKey: string,
  params?: {
    page: number
    pageSize: number
  },
  loading = false
) => {
  try {
    const wait = (ms: number) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms)
      })

    const resolveConversationKey = async () => {
      const MAX_RETRIES = 8
      const RETRY_DELAY_MS = 250

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        const keys = JSON.parse(
          localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) ||
            "{}"
        )
        const key = keys[conversationId]
        if (key && key.length === 64) {
          return key as string
        }
        await wait(RETRY_DELAY_MS)
      }
      return ""
    }

    const response: AxiosResponse<{
      data: ConversationMessagesDetail
    }> = await Axios.get(
      `/api/conversation/${conversationId}/messages?${
        loading ? `loading=0` : ""
      }`,
      {
        params,
        headers: dbKey
          ? {
              "x-db-key": dbKey,
            }
          : undefined,
      }
    )

    const conversationKey = await resolveConversationKey()
    setTabCurrentConversationEncryptionKey(conversationKey || "")

    const replyKeyConversation = response.data.data.replyKeyConversation

    addBreadcrumb("decrypt", "Decrypting conversation messages", "info", {
      message_count: response.data.data.messages.length,
    })
    const decryptedMessages = await Promise.all(
      response.data.data.messages.map(async (item) => {
        const messageKey = item.key ? replyKeyConversation : conversationKey
        let decrypted = item.content
        if (messageKey) {
          try {
            const result = (await encryptionGCMHelper.decryptContext(
              messageKey,
              item.content
            )) as string
            if (result === item.content) {
              const trcCode = generateTRCCode("DEC")
              addBreadcrumb("decrypt", "Conversation message decrypt failed", "warning")
              captureSentryEncryptionEvent(
                "Content decryption failed - WRONG_VALUE",
                {
                  error_category: "DEC",
                  error_type: "WRONG_VALUE",
                  ...getSentryUserContext(),
                  error_code: trcCode,
                  step_trail:
                    "conversations.api → getConversationMessagesMiddleware",
                }
              )
              decrypted = DECRYPTION_BLOCKED_CONTENT
            } else {
              decrypted = result
            }
          } catch {
            decrypted = item.content
          }
        }
        const newCommentFrom = item.comment_from
        if (newCommentFrom) {
          const commentKey = item.comment_from?.key
            ? replyKeyConversation
            : messageKey
          if (commentKey) {
            try {
              const result = (await encryptionGCMHelper.decryptContext(
                commentKey,
                newCommentFrom.content
              )) as string
              if (result === newCommentFrom.content) {
                newCommentFrom.content = DECRYPTION_BLOCKED_CONTENT
              } else {
                newCommentFrom.content = result
              }
            } catch {
              // keep original
            }
          }
        }
        return {
          ...item,
          content: decrypted,
          originContent: decrypted || item.content,
          messageKey: (messageKey as string) || "",
          comment_from: newCommentFrom,
        }
      })
    )
    return { ...response.data.data, messages: decryptedMessages }
  } catch (error) {
    return emptyConversationMessagesDetail
  }
}

export const putConversationEncryptPrivateKey = (
  conversationId: string,
  encryptPrivateKey: string
) => {
  return Axios.put(`/api/conversation/${conversationId}/encrypt-private-key`, {
    encryptPrivateKey,
  })
}

export const getConversationKeyMiddleware = async (conversationId: string) => {
  const response: AxiosResponse<ConversationShareKeys> = await Axios.get(
    `/api/conversation/${conversationId}/key`
  )

  return response.data
}
export const getConversationKeysMiddleware = async () => {
  const response: AxiosResponse<ConversationKeyResponse> = await Axios.get(
    `/api/conversation/keys`
  )

  return response.data.data
}
export type SharedKeys = {
  data: {
    conversationKey?: string
    projectKey: string
  }
  statusCode: number
}
export const getSharedKeysMiddleware = async (
  email: string,
  projectId: string,
  conversationId?: string
) => {
  const response: AxiosResponse<SharedKeys> = await Axios.get(
    `/api/encryption/shared-keys?email=${email}&project_id=${projectId}${
      conversationId ? `&conversation_id=${conversationId}` : ""
    }`
  )

  return response.data.data
}

export const postConversationLikeBuildMiddleware = (
  idProjectBuild: string,
  callback: (type: STATUS_RESPONSE, messenger: string) => void
) => {
  Axios.post(`/api/conversation/like-build/${idProjectBuild}`)
    .then(() => {
      callback(STATUS_RESPONSE.SUCCESS, "")
    })
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.FAVORITE_PROJECT_BUILD_ERROR
      )
    })
}

export const postConversationSendMessageMiddleware = async (
  worker: Worker,
  conversationId: string,
  approveIds: string[],
  dbKey: string,
  data: {
    content: string
    files: {
      file_name: string
      file: File
      file_type: string
      key: string
    }[]
    comment_from?: string
  },
  callback: (type: STATUS_RESPONSE, messenger: string) => void
) => {
  const conversationEncryptionKeys = JSON.parse(
    localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
  )

  const conversationKey = conversationEncryptionKeys[conversationId] || ""
  if (!conversationId) {
    callback(
      STATUS_RESPONSE.ERROR,
      MESSENGER_NOTIFICATION.CONVERSATION_SEND_MESSAGE_ERROR
    )
    return
  }

  const newFilesTimestamp = data.files.map((file) => {
    return {
      ...file,
      file: new File(
        [file.file],
        `${getFileNameWithTimestamp(file.file_name, Date.now())}`
      ),
    }
  })

  worker.postMessage({
    action: ACTION_WORKER.START,
    payload: {
      files: newFilesTimestamp,
      content: data.content,
      userId: localStorage.getItem("user_id") || "",
      encryptionKey: conversationKey,
      componentType: "conversation",
      componentHistoryId: conversationId,
      isRawFile: false,
      token: getAccessTokenInLocalStorage(),
      dbKey,
    },
  })

  worker.onmessage = async function (e) {
    if (e.data.action === ACTION_WORKER.FINISH) {
      const { fileHashs, contentKey, fileErrors } = e.data.payload
      if (
        JSON.stringify(fileErrors) === JSON.stringify(["Private key not found"])
      ) {
        localStorage.clear()
        window.location.href = "/login"
        return
      }

      if (!isUndefined(fileErrors) && fileErrors.length > 0) {
        callback(
          STATUS_RESPONSE.ERROR,
          `${fileErrors.map((el) => el).join(", ")}`
        )
        return
      }

      const newHash = fileHashs.map((el) => {
        const findKey = newFilesTimestamp.find(
          (fileTime) => fileTime.file.name === el.file_name
        )
        return {
          ...el,
          file_key: findKey?.key || "",
        }
      })

      let newRawContent = replaceImagesWithText(
        data.content,
        data.files.map((el) => {
          return {
            key: el.key,
            file_name: el.file_name,
          }
        })
      )
      if (data.files.length) {
        newRawContent = `${newRawContent}<br><span style="font-style: italic;color: #7a7a7a;font-size: 12px;">Please go to the conversation to download/view the attachments.</span>`
      }

      const inlinedHtml = juice.inlineContent(
        prepareEditorContentForEmail(
          `<div class="message-item"> ${newRawContent}</div>`
        ),
        messageConversationCss
      )

      const dataRequest: {
        files: any[]
        content: string
        raw_content: string
        log: string
        comment_from?: string
      } = {
        files: newHash,
        content: contentKey,
        raw_content: inlinedHtml,
        log: await activityLogHelper.toEncryptedMessage(
          ActivityLogType.SendInvitation,
          {}
        ),
      }
      if (data.comment_from) {
        dataRequest.comment_from = data.comment_from
      }
      const multipart = new FormData()
      multipart.append("data", JSON.stringify(dataRequest))
      Axios.post(
        `/api/conversation/${conversationId}/send-message`,
        multipart,
        {
          headers: dbKey
            ? {
                "x-db-key": dbKey,
              }
            : undefined,
        }
      )
        .then(async (response: AxiosResponse) => {
          callback(STATUS_RESPONSE.SUCCESS, "")
          if (response.data.data?.message_id && approveIds.length > 0) {
            await postApprovalLogMiddleWare(
              {
                comment_id: response.data.data.message_id,
                model: MODEL_ACTIVITY_LOG.CONVERSATION_BUILDS,
                status: ApprovalStatus.REQUIRED,
                user_ids: JSON.stringify(approveIds),
              },
              dbKey
            )
          }
          await Promise.all(
            response.data.data.files.map(async (file) => {
              await encryptionGCMHelper.verifySignatureFileByGCM(
                conversationKey,
                file.file_key,
                getAccessTokenInLocalStorage(),
                {
                  conversation_id: conversationId,
                }
              )
              return true
            })
          )
        })
        .catch((error) => {
          callback(
            STATUS_RESPONSE.ERROR,
            error.response?.data?.message ??
              MESSENGER_NOTIFICATION.CONVERSATION_SEND_MESSAGE_ERROR
          )
        })
    }
  }
}
export const postAddInviteeColumnMiddleware = async (
  conversationId: string,
  projectComponentHistoryId: string,
  dataRequest: InviteeAddColumnDetail[],
  oldData: InviteeAddColumnDetail[],
  isUpdateColumn: boolean,
  callback: (type: STATUS_RESPONSE, messenger: string) => void
) => {
  const diffColumn = activityLogHelper.toDiffColumn(oldData, dataRequest)
  const log = activityLogHelper.createAddColumnLog(
    ComponentType.BOM,
    diffColumn
  )
  const newData = await Promise.all(
    dataRequest.map(async (item) => {
      const encryptedValues = await encryptionController().encrypt(
        JSON.stringify(item.values),
        { dataType: "string", type: "project" }
      )
      return {
        ...item,
        values: encryptedValues,
      }
    })
  )
  Axios.post(
    `/api/bom/${projectComponentHistoryId}/${conversationId}/add-invitee-column`,
    {
      data: newData,
      log: await activityLogHelper.toEncryptedMessage(
        ActivityLogType.AddBomColumn,
        {
          content: log,
        }
      ),
    }
  )
    .then(() => {
      callback(
        STATUS_RESPONSE.SUCCESS,
        `${isUpdateColumn ? "Updated" : "Added"} column successfully!`
      )
    })
    .catch((error) => {
      const newMessageError = `${
        isUpdateColumn ? "Updated" : "Added"
      } column failed!`
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message
          ? error.response?.data?.message
          : newMessageError
      )
    })
}

export const getNotificationConversationMiddleware = async (
  conversationId: string,
  dbKey: string
) => {
  const response: AxiosResponse<{
    data: {
      is_notified: boolean
    }
  }> = await Axios.get(`/api/conversation/${conversationId}/is-notify`, {
    headers: dbKey
      ? {
          "x-db-key": dbKey,
        }
      : undefined,
  })
  return response.data.data
}
export const postNotificationConversationMiddleware = (
  conversationId: string,
  label: string,
  callback: (type: STATUS_RESPONSE, messenger: string) => void
) => {
  Axios.post(`/api/conversation/${conversationId}/update-is-notify`)
    .then(() => {
      callback(
        STATUS_RESPONSE.SUCCESS,
        `Turn ${label} email notification successfully!`
      )
    })
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message
          ? error.response?.data?.message
          : `Turn ${label} email notification fail!`
      )
    })
}

export const archiveConversationMiddleware = async (
  conversationId: string,
  dbKey?: string
) => {
  const res = await Axios.post(
    `/api/conversation/archive/${conversationId}`,
    undefined,
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : {},
    }
  )
  return res.data
}
export const getCustomStatusesMiddleware = async (dbKey: string) => {
  const res = await Axios.get(
    "/api/conversation/custom-statuses/get?loading=0",
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data.data
}
export const createCustomStatusMiddleware = async (
  customStatus: CustomStatus,
  dbKey?: string
) => {
  const res: AxiosResponse<{
    data: CustomStatus
  }> = await Axios.post(
    `/api/conversation/custom-status/create`,
    {
      custom_status: { name: customStatus.name, color: customStatus.color },
    },
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data.data
}
export const updateCustomStatusesMiddleware = async (
  customStatus: CustomStatus,
  dbKey?: string
) => {
  const res = await Axios.post(
    `/api/conversation/custom-statuses/${customStatus.id}/update`,
    {
      custom_status: { name: customStatus.name, color: customStatus.color },
    },
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data
}
export const deleteCustomstatusMiddleware = async (
  customStatus: CustomStatus,
  dbKey?: string
) => {
  const res = await Axios.delete(
    `/api/conversation/custom-status/${customStatus.id}/delete`,
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data
}
export const updateCustomStatusMiddleware = async (
  conversationId: string,
  customStatus?: CustomStatus,
  dbKey?: string
) => {
  const res = await Axios.post(
    `/api/conversation/${conversationId}/custom-status`,
    { custom_status_id: customStatus?.id },
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data
}
export const updateNotesMiddleware = async (
  conversationId: string,
  notes: string,
  dbKey?: string
) => {
  const encryptedNote = await encryptionController().encrypt(notes, {
    type: "conversation_note",
    dataType: "string",
  })
  const res = await Axios.post(
    `/api/conversation/notes/${conversationId}`,
    {
      notes: encryptedNote,
    },
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data
}
export const getUserNoteFilesMiddleware = async (): Promise<{
  files: { file_key: string; is_synced: boolean }[]
  notes: {
    conversation_id: string
    note: string
    id: string
    is_synced: boolean
  }[]
}> => {
  const res = await Axios.get(`/api/conversation/user-note-files?loading=0`)
  return res.data
}
export const postSyncNoteFileMiddleware = async (payload: any) => {
  let url = `/api/conversation/sync-note-file?loading=0`
  return Axios.post(url, payload)
}

export const deleteNoteImageMiddleware = async (
  conversationId: string,
  fileKey: string,
  dbKey?: string
) => {
  const res = await Axios.delete(
    `/api/conversation/notes/${conversationId}/delete-image/${encodeURIComponent(
      fileKey
    )}`,
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : undefined,
    }
  )
  return res.data.data
}

export const deleteNotesFolder = async (conversationId: string) => {
  const res = await Axios.delete(
    `/api/conversation/${conversationId}/notes/delete-folder`
  )
  return res.data
}

export const likeConversationMiddleware = async (
  conversationId: string,
  dbKey?: string
) => {
  const res = await Axios.post(
    `/api/conversation/like/${conversationId}`,
    undefined,
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : {},
    }
  )
  return res.data
}

export const getNoteMiddleware = async (
  conversationId: string,
  dbKey: string
) => {
  const res: AxiosResponse<{
    data: Note
  }> = await Axios.get(`/api/conversation/get-notes/${conversationId}`, {
    headers: dbKey
      ? {
          "x-db-key": dbKey,
        }
      : {},
  })
  const userVaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
  let decryptedNote = await encryptionController().decrypt(res.data.data.note, {
    dataType: "string",
    type: "conversation_note",
    encryptionKey: userVaultKey,
  })
  const fileDatas = await Promise.all(
    res.data.data.files.map(async (file) => {
      const base64Str = await encryptionGCMHelper.getFileImageFromEncrypted(
        file.file,
        userVaultKey,
        getAccessTokenInLocalStorage(),
        {},
        "base64",
        undefined,
        dbKey
      )
      return {
        ...file,
        url: base64Str as string,
      }
    })
  )
  const includeImageIntoText = (content: string, files: any) => {
    let newContent = content
    files.forEach((rawFile) => {
      newContent = newContent.replaceAll(rawFile.file, rawFile.url)
    })
    return newContent
  }
  const noteWithImages = includeImageIntoText(decryptedNote, fileDatas)
  return {
    ...res.data.data,
    files: fileDatas,
    note: noteWithImages,
  }
}

export const getConversationRoleMiddleWare = async (
  projectBuildComponentId: string,
  conversationId: string,
  dbKey?: string
) => {
  const res: AxiosResponse<{
    data: {
      role: CONVERSATION_ROLE
    }
  }> = await Axios.get(
    `/api/conversation/${projectBuildComponentId}/${conversationId}/get-role`,
    {
      headers: dbKey
        ? {
            "x-db-key": dbKey,
          }
        : {},
    }
  )
  return res.data.data
}

export const backupComponentInviteeMiddleWare = async (
  dataComponent: Record<string, SendComponentInviteeRequest>,
  conversationId: string
) => {
  const res: AxiosResponse<{
    data: {
      original_project_component_history_id: string
      backup_project_component_history_id: string
    }[]
  }> = await Axios.post(`/api/project-build/invitee/backup-component`, {
    data: dataComponent,
    conversation_id: conversationId,
  })
  return res.data.data
}

export const backupMessageInviteeMiddleWare = async (
  dataRequest: any,
  conversationId: string
) => {
  const res: AxiosResponse<{
    data: string[]
  }> = await Axios.post(`/api/conversation/${conversationId}/backup-message`, {
    data: dataRequest,
  })
  return res.data.data
}

export const getListApproveConversationMiddleware = async (
  conversationId: string,
  dbKey: string
) => {
  try {
    const res = await Axios.get(
      `/api/conversation/${conversationId}/assign-approvers?loading=0`,
      {
        headers: dbKey
          ? {
              "x-db-key": dbKey,
            }
          : {},
      }
    )
    return res.data.data
  } catch (error) {
    return []
  }
}

export const postApprovalLogMiddleWare = async (
  dataRequest: {
    comment_id: string
    model: string
    status: ApprovalStatus
    user_ids: string
  },
  dbKey: string,
  isLoading = true
) => {
  try {
    const res = await Axios.post(
      `/api/log/approval-log${isLoading ? "?loading=0" : ""}`,
      {
        ...dataRequest,
      },
      {
        headers: dbKey
          ? {
              "x-db-key": dbKey,
            }
          : {},
      }
    )
    return res.data.data
  } catch (error) {
    return null
  }
}
